import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
    forwardRef,
} from "react";
import {
    UploadCloud,
    FileText,
    Trash2,
    Download,
    Loader2,
    X,
    CheckCircle2,
    AlertCircle,
    Archive,
    ShieldCheck,
    RefreshCw,
    Eye,
    Image as ImageIcon,
    FileCheck,
    Car,
    ClipboardList,
    Sparkles,
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { crud, absoluteUrl, api } from "../lib/api";
import { EVRAK_TURLERI, EVRAK_ONAY_DURUMU } from "../lib/choices";
import {
    detectEvrakTuruFromFilename,
    evrakTuruLabel,
} from "../lib/evrakTuruDetect";
import { PANEL_SELECT } from "../lib/panelFormStyles";
import SigortaVekaletBridge from "./SigortaVekaletBridge";

function turLabel(value) {
    return EVRAK_TURLERI.find((t) => t.value === value)?.label || value || "—";
}

function onayLabel(value) {
    return EVRAK_ONAY_DURUMU.find((t) => t.value === value)?.label || value || "—";
}

function onayClass(durum) {
    if (durum === "ONAYLI") return "bg-emerald-500/15 text-emerald-300 border-emerald-500/30";
    if (durum === "REDDEDILDI") return "bg-rose-500/15 text-rose-300 border-rose-500/30";
    return "bg-amber-500/15 text-amber-200 border-amber-500/30";
}

function bytesToHuman(b) {
    if (!b) return "";
    const u = ["B", "KB", "MB"];
    let i = 0;
    let n = b;
    while (n > 1024 && i < u.length - 1) {
        n /= 1024;
        i++;
    }
    return `${n.toFixed(n >= 10 ? 0 : 1)} ${u[i]}`;
}

function evrakTypeMeta(tur) {
    const map = {
        VEKALET: { icon: ShieldCheck, tone: "violet", label: "Vekalet" },
        RUHSAT: { icon: Car, tone: "sky", label: "Ruhsat" },
        TUTANAK: { icon: ClipboardList, tone: "amber", label: "Tutanak" },
        KAZA_RESIM: { icon: ImageIcon, tone: "emerald", label: "Kaza Resmi" },
        EKSPER: { icon: FileCheck, tone: "cyan", label: "Eksper" },
        DILEKCE: { icon: FileText, tone: "slate", label: "Dilekçe" },
    };
    return map[tur] || { icon: FileText, tone: "slate", label: turLabel(tur) };
}

const TONE_ICON = {
    violet: "bg-violet-500/20 border-violet-500/30 text-violet-300",
    sky: "bg-sky-500/20 border-sky-500/30 text-sky-300",
    amber: "bg-amber-500/20 border-amber-500/30 text-amber-300",
    emerald: "bg-emerald-500/20 border-emerald-500/30 text-emerald-300",
    cyan: "bg-cyan-500/20 border-cyan-500/30 text-cyan-300",
    slate: "bg-slate-500/15 border-white/10 text-slate-400",
};

function MiniStat({ label, value, tone = "slate" }) {
    const tones = {
        violet: "border-violet-500/25 bg-violet-500/10 text-violet-200",
        emerald: "border-emerald-500/25 bg-emerald-500/10 text-emerald-200",
        amber: "border-amber-500/25 bg-amber-500/10 text-amber-200",
        slate: "border-white/10 bg-[#0B0F19]/60 text-slate-300",
    };
    return (
        <div className={`rounded-lg border px-3 py-2 text-center ${tones[tone] || tones.slate}`}>
            <div className="text-[10px] uppercase tracking-wide opacity-75">{label}</div>
            <div className="text-lg font-bold mt-0.5">{value}</div>
        </div>
    );
}

/**
 * Sigorta dosyası formuna gömülü evrak yöneticisi — koyu tema, sürükle-bırak, vekalet köprüsü.
 */
const EvrakSection = forwardRef(function EvrakSection(
    { parentId, relationField = "sigorta_dosyasi" },
    ref,
) {
    const isQueueMode = !parentId;
    const isSigortaDosya = relationField === "sigorta_dosyasi";
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(!isQueueMode);
    const [pending, setPending] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [editingTurId, setEditingTurId] = useState(null);
    const [paketUrl, setPaketUrl] = useState("");
    const [paketTarih, setPaketTarih] = useState("");
    const [vekaletSyncing, setVekaletSyncing] = useState(false);
    const inputRef = useRef(null);
    const pendingRef = useRef(pending);

    useEffect(() => {
        pendingRef.current = pending;
    }, [pending]);

    const stats = useMemo(() => {
        const onayli = docs.filter((d) => d.onay_durumu === "ONAYLI").length;
        const vekalet = docs.filter(
            (d) => d.evrak_turu === "VEKALET" || d.tur === "VEKALET",
        ).length;
        const bekleyenVekalet = docs.filter(
            (d) =>
                (d.evrak_turu === "VEKALET" || d.tur === "VEKALET") &&
                !d.vekalet_senkron,
        ).length;
        return { onayli, vekalet, bekleyenVekalet };
    }, [docs]);

    const loadPaket = useCallback(async () => {
        if (!parentId || !isSigortaDosya) {
            setPaketUrl("");
            setPaketTarih("");
            return;
        }
        try {
            const d = await crud.retrieve("sigorta-dosyalari", parentId);
            setPaketUrl(d?.evrak_paketi ? absoluteUrl(d.evrak_paketi) : "");
            setPaketTarih(d?.evrak_paketi_guncelleme || "");
        } catch {
            setPaketUrl("");
            setPaketTarih("");
        }
    }, [parentId, isSigortaDosya]);

    const loadDocs = useCallback(async () => {
        if (!parentId) {
            setDocs([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const data = await crud.list("evraklar", {
                [relationField]: parentId,
                page_size: 200,
            });
            const items = Array.isArray(data) ? data : data.results || [];
            setDocs(
                items.filter((d) => {
                    const fk = d?.[relationField];
                    if (fk == null) return true;
                    if (typeof fk === "object") return fk?.id === parentId;
                    return String(fk) === String(parentId);
                }),
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Evraklar yüklenemedi.");
            setDocs([]);
        } finally {
            setLoading(false);
            loadPaket();
        }
    }, [parentId, relationField, loadPaket]);

    useEffect(() => {
        loadDocs();
    }, [loadDocs]);

    async function vekaletSenkron() {
        if (!parentId || !isSigortaDosya) return;
        setVekaletSyncing(true);
        try {
            const r = await api.post(
                `/v1/admin/sigorta-dosyalari/${parentId}/vekalet-senkron/`,
            );
            toast.success(
                `${r.data?.olusturulan || 0} vekalet dosyası modüle aktarıldı`,
            );
            await loadDocs();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Vekalet senkronu başarısız");
        } finally {
            setVekaletSyncing(false);
        }
    }

    function addFiles(fileList) {
        const arr = Array.from(fileList || []);
        if (!arr.length) return;
        setPending((p) => [
            ...p,
            ...arr.map((file) => {
                const tur = detectEvrakTuruFromFilename(file.name);
                return {
                    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                    file,
                    tur,
                    autoTur: tur,
                    progress: 0,
                    error: null,
                    done: false,
                };
            }),
        ]);
    }

    function updatePending(id, patch) {
        setPending((arr) => arr.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    }

    async function uploadOne(item, parentIdOverride) {
        const pid = parentIdOverride ?? parentId;
        if (!pid) {
            updatePending(item.id, { error: "Üst kayıt henüz oluşturulmamış." });
            return { ok: false };
        }
        try {
            updatePending(item.id, { progress: 1, error: null });
            await crud.upload(
                "evraklar",
                {
                    [relationField]: pid,
                    dosya: item.file,
                    evrak_turu: item.tur,
                    onay_durumu: "ONAYLI",
                },
                (e) => {
                    if (e.total) {
                        updatePending(item.id, {
                            progress: Math.min(
                                99,
                                Math.round((e.loaded / e.total) * 100),
                            ),
                        });
                    }
                },
            );
            updatePending(item.id, { progress: 100, done: true });
            return { ok: true };
        } catch (e) {
            const data = e?.response?.data;
            const msg =
                data && typeof data === "object"
                    ? Object.entries(data)
                          .map(
                              ([k, v]) =>
                                  `${k}: ${Array.isArray(v) ? v.join(", ") : v}`,
                          )
                          .join(" · ")
                    : data?.detail || e?.message || "Yükleme başarısız.";
            updatePending(item.id, { error: msg, progress: 0 });
            return { ok: false };
        }
    }

    async function uploadAll() {
        if (!pending.length || isQueueMode) return;
        setUploading(true);
        let ok = 0;
        let fail = 0;
        for (const item of pending) {
            if (item.done) continue;
            const r = await uploadOne(item);
            if (r.ok) ok++;
            else fail++;
        }
        setUploading(false);
        if (ok > 0) {
            toast.success(`${ok} evrak yüklendi`);
            setPending((arr) => arr.filter((p) => !p.done));
            loadDocs();
            if (isSigortaDosya && pending.some((p) => p.tur === "VEKALET")) {
                setTimeout(() => vekaletSenkron(), 800);
            }
        }
        if (fail > 0 && ok === 0) toast.error(`${fail} evrak yüklenemedi`);
    }

    useImperativeHandle(
        ref,
        () => ({
            hasQueue: () => pendingRef.current.some((p) => !p.done && !p.error),
            flushQueue: async (newParentId) => {
                if (!newParentId) return { success: 0, failed: 0 };
                const items = pendingRef.current.filter((p) => !p.done);
                let success = 0;
                let failed = 0;
                for (const it of items) {
                    const r = await uploadOne(it, newParentId);
                    if (r.ok) success++;
                    else failed++;
                }
                return { success, failed };
            },
        }),
        [relationField],
    );

    async function deleteDoc(doc) {
        if (!window.confirm(`"${turLabel(doc.evrak_turu)}" evrak silinsin mi?`)) return;
        try {
            await crud.remove("evraklar", doc.id);
            toast.success("Evrak silindi");
            loadDocs();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Silinemedi.");
        }
    }

    function docUrl(doc) {
        return absoluteUrl(doc?.dosya || doc?.dosya_url);
    }

    const pendingCount = pending.filter((p) => !p.done).length;

    return (
        <div className="md:col-span-2 rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-emerald-500/6 via-[#0B0F19]/98 to-[#121A2F]/95 overflow-hidden shadow-xl shadow-black/25">
            {/* Başlık */}
            <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-r from-emerald-500/10 via-[#D4AF37]/6 to-transparent">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-xl bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0">
                            <FileText className="h-5 w-5 text-emerald-300" />
                        </div>
                        <div>
                            <div className="text-base font-bold text-slate-100 flex items-center gap-2">
                                Evraklar
                                <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                {isQueueMode
                                    ? "Kayıt sonrası seçilen evraklar otomatik yüklenir ve ZIP arşivine alınır."
                                    : "Dosya adından tür otomatik belirlenir · vekalet evrakları modüle senkron edilir."}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-slate-500 px-2 py-1 rounded-md border border-white/10 bg-[#0B0F19]/60">
                            {isQueueMode
                                ? `Kuyruk: ${pendingCount}`
                                : `${docs.length} kayıt`}
                        </span>
                        {!isQueueMode && stats.bekleyenVekalet > 0 && (
                            <button
                                type="button"
                                disabled={vekaletSyncing}
                                onClick={vekaletSenkron}
                                className="h-9 px-3 rounded-lg border border-violet-500/35 bg-violet-600/80 hover:bg-violet-600 text-white text-xs font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                {vekaletSyncing ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-3.5 w-3.5" />
                                )}
                                Vekalet senkron ({stats.bekleyenVekalet})
                            </button>
                        )}
                        {!isQueueMode && paketUrl && (
                            <a
                                href={paketUrl}
                                download
                                className="h-9 px-3 rounded-lg border border-emerald-500/35 bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-200 text-xs font-semibold inline-flex items-center gap-2"
                            >
                                <Archive className="h-3.5 w-3.5" />
                                ZIP arşivi
                            </a>
                        )}
                        <button
                            type="button"
                            onClick={() => inputRef.current?.click()}
                            className="h-9 px-3 rounded-lg border border-[#D4AF37]/35 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 text-[#F3E5AB] text-xs font-semibold inline-flex items-center gap-2"
                        >
                            <UploadCloud className="h-4 w-4" />
                            Dosya Ekle
                        </button>
                    </div>
                </div>
                {paketTarih && (
                    <p className="mt-2 text-[10px] text-slate-500 pl-14">
                        Arşiv güncelleme:{" "}
                        {new Date(paketTarih).toLocaleString("tr-TR")}
                    </p>
                )}
            </div>

            {isQueueMode && (
                <p className="mx-5 mt-3 text-xs text-amber-200/90 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">
                    Dosyayı kaydettikten sonra seçtiğiniz evraklar otomatik yüklenir;
                    ardından tüm evraklar ZIP arşivine alınır.
                </p>
            )}

            {!isQueueMode && isSigortaDosya && parentId && (
                <div className="px-5 pt-3">
                    <SigortaVekaletBridge dosyaId={parentId} />
                </div>
            )}

            {!isQueueMode && docs.length > 0 && (
                <div className="px-5 py-3 grid grid-cols-2 sm:grid-cols-4 gap-2 border-b border-white/5">
                    <MiniStat label="Toplam" value={docs.length} />
                    <MiniStat label="Onaylı" value={stats.onayli} tone="emerald" />
                    <MiniStat label="Vekalet" value={stats.vekalet} tone="violet" />
                    <MiniStat
                        label="Senkron bekliyor"
                        value={stats.bekleyenVekalet}
                        tone={stats.bekleyenVekalet > 0 ? "amber" : "slate"}
                    />
                </div>
            )}

            {/* Mevcut evrak listesi */}
            {!isQueueMode && (
                <div className="px-5 pt-3">
                    {loading ? (
                        <div className="py-6 text-center text-sm text-slate-500 flex justify-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Evraklar yükleniyor…
                        </div>
                    ) : docs.length === 0 ? (
                        <p className="text-sm text-slate-500 py-3 text-center">
                            Henüz evrak yok — aşağıdan dosya ekleyin.
                        </p>
                    ) : (
                        <ul className="space-y-2 mb-3">
                            {docs.map((d) => {
                                const url = docUrl(d);
                                const onay = d.onay_durumu || "BEKLIYOR";
                                const tur = d.evrak_turu || d.tur;
                                const meta = evrakTypeMeta(tur);
                                const Icon = meta.icon;
                                const iconCls =
                                    TONE_ICON[meta.tone] || TONE_ICON.slate;
                                const dosyaAd =
                                    d.dosya_adi ||
                                    (typeof d.dosya === "string"
                                        ? d.dosya.split("/").pop()
                                        : d.dosya?.name?.split("/").pop()) ||
                                    turLabel(tur);

                                return (
                                    <li
                                        key={d.id}
                                        className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-white/10 bg-[#0B0F19]/70 hover:border-white/20 transition text-sm group"
                                    >
                                        <div
                                            className={`h-9 w-9 rounded-lg border flex items-center justify-center shrink-0 ${iconCls}`}
                                        >
                                            <Icon className="h-4 w-4" />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-slate-100 truncate">
                                                {turLabel(tur)}
                                            </div>
                                            <div className="text-[10px] text-slate-500 truncate">
                                                {dosyaAd}
                                            </div>
                                            <div className="flex flex-wrap items-center gap-2 mt-1">
                                                <span
                                                    className={`text-[10px] px-1.5 py-0.5 rounded border ${onayClass(onay)}`}
                                                >
                                                    {onayLabel(onay)}
                                                </span>
                                                {(d.yuklenme_tarihi ||
                                                    d.created_at) && (
                                                    <span className="text-[10px] text-slate-500">
                                                        {new Date(
                                                            d.yuklenme_tarihi ||
                                                                d.created_at,
                                                        ).toLocaleDateString("tr-TR")}
                                                    </span>
                                                )}
                                                {d.dosya_boyutu && (
                                                    <span className="text-[10px] text-slate-600">
                                                        {bytesToHuman(d.dosya_boyutu)}
                                                    </span>
                                                )}
                                                {tur === "VEKALET" &&
                                                    (d.vekalet_senkron &&
                                                    d.vekalet_id ? (
                                                        <Link
                                                            to={`/e/vekaletler/${d.vekalet_id}`}
                                                            className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-violet-500/35 bg-violet-500/15 text-violet-200 hover:underline"
                                                        >
                                                            <ShieldCheck className="h-3 w-3" />
                                                            Vekaletler modülünde
                                                        </Link>
                                                    ) : (
                                                        <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-200">
                                                            Senkron bekliyor
                                                        </span>
                                                    ))}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0 opacity-80 group-hover:opacity-100">
                                            {url && (
                                                <>
                                                    <a
                                                        href={url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-white/10 text-slate-400 hover:text-slate-200"
                                                        title="Önizle"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </a>
                                                    <a
                                                        href={url}
                                                        download
                                                        className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-emerald-500/10 text-slate-400 hover:text-emerald-300"
                                                        title="İndir"
                                                    >
                                                        <Download className="h-4 w-4" />
                                                    </a>
                                                </>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => deleteDoc(d)}
                                                className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-rose-500/10 text-slate-400 hover:text-rose-400"
                                                title="Sil"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}

            {/* Yükleme alanı */}
            <div className="px-5 py-4 space-y-3 border-t border-white/5">
                <p className="text-[11px] text-slate-500 leading-relaxed">
                    Evrak türü dosya adından otomatik belirlenir (ör.{" "}
                    <span className="text-slate-300">Ruhsat.pdf</span>,{" "}
                    <span className="text-slate-300">Vekaletname.pdf</span>,{" "}
                    <span className="text-slate-300">Kaza Resimleri.zip</span>
                    ). Yanlışsa kuyruktan düzeltebilirsiniz.
                </p>

                <div
                    onDragEnter={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                    }}
                    onDragOver={(e) => e.preventDefault()}
                    onDragLeave={() => setDragActive(false)}
                    onDrop={(e) => {
                        e.preventDefault();
                        setDragActive(false);
                        addFiles(e.dataTransfer?.files);
                    }}
                    onClick={() => inputRef.current?.click()}
                    className={`w-full rounded-xl border-2 border-dashed px-4 py-6 text-center transition cursor-pointer ${
                        dragActive
                            ? "border-emerald-400/60 bg-emerald-500/10"
                            : "border-white/10 bg-[#121A2F]/60 hover:border-[#D4AF37]/30 hover:bg-[#121A2F]/80"
                    }`}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                            addFiles(e.target.files);
                            e.target.value = "";
                        }}
                    />
                    <UploadCloud className="h-7 w-7 mx-auto text-emerald-400/80 mb-2" />
                    <p className="text-sm text-slate-200 font-medium">
                        Dosya seçin veya sürükleyin
                    </p>
                    <p className="text-[10px] text-slate-500 mt-1">
                        PDF, resim, ZIP — çoklu seçim desteklenir
                    </p>
                </div>

                {pending.length > 0 && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <span className="text-xs text-slate-500">
                                Yüklenecek ({pendingCount})
                            </span>
                            {!isQueueMode && (
                                <button
                                    type="button"
                                    disabled={uploading}
                                    onClick={uploadAll}
                                    className="h-8 px-3 rounded-lg bg-[#D4AF37] hover:bg-[#C9A227] text-[#0B0F19] text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60"
                                >
                                    {uploading ? (
                                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                    ) : null}
                                    {uploading ? "Yükleniyor…" : "Yükle"}
                                </button>
                            )}
                        </div>
                        <ul className="space-y-1.5">
                            {pending.map((p) => (
                                <li
                                    key={p.id}
                                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/10 bg-[#0B0F19]/80"
                                >
                                    {p.done ? (
                                        <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
                                    ) : p.error ? (
                                        <AlertCircle className="h-4 w-4 text-rose-400 shrink-0" />
                                    ) : (
                                        <FileText className="h-4 w-4 text-slate-500 shrink-0" />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <p className="text-xs text-slate-200 truncate">
                                            {p.file.name}
                                        </p>
                                        <p className="text-[10px] text-slate-500">
                                            {bytesToHuman(p.file.size)}
                                            {p.error ? ` · ${p.error}` : ""}
                                            {p.progress > 0 && p.progress < 100
                                                ? ` · %${p.progress}`
                                                : ""}
                                        </p>
                                    </div>
                                    <div className="shrink-0 flex flex-col items-end gap-1 max-w-[11rem]">
                                        {editingTurId === p.id ? (
                                            <select
                                                value={p.tur}
                                                autoFocus
                                                onChange={(e) =>
                                                    updatePending(p.id, {
                                                        tur: e.target.value,
                                                    })
                                                }
                                                onBlur={() => setEditingTurId(null)}
                                                className={`${PANEL_SELECT} text-xs py-1 max-w-full`}
                                            >
                                                {EVRAK_TURLERI.map((t) => (
                                                    <option
                                                        key={t.value}
                                                        value={t.value}
                                                    >
                                                        {t.label}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : (
                                            <>
                                                <span
                                                    className="text-[10px] px-2 py-0.5 rounded border bg-[#121A2F] border-[#D4AF37]/30 text-[#F3E5AB] truncate max-w-full"
                                                    title={evrakTuruLabel(p.tur)}
                                                >
                                                    {evrakTuruLabel(p.tur)}
                                                </span>
                                                {p.tur === p.autoTur && (
                                                    <span className="text-[9px] text-slate-600">
                                                        otomatik
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        setEditingTurId(p.id)
                                                    }
                                                    className="text-[10px] text-[#D4AF37] hover:underline"
                                                >
                                                    Türü düzelt
                                                </button>
                                            </>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setPending((arr) =>
                                                arr.filter((x) => x.id !== p.id),
                                            )
                                        }
                                        className="h-7 w-7 shrink-0 flex items-center justify-center rounded hover:bg-white/10 text-slate-500"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
});

export default EvrakSection;
